import { useCallback, useEffect, useMemo, useState } from 'react';
import { getSessionStatus, getLlmModeStatus, downloadPdf, downloadCsv, notifyCreditsChanged } from '../services/api';

function openPendingDocumentTab() {
  try {
    const tab = window.open('about:blank', '_blank');
    if (tab) {
      tab.opener = null;
      tab.document.title = 'Preparing document...';
      tab.document.body.innerHTML = '<p style="font-family: sans-serif; padding: 1rem;">Preparing document...</p>';
    }
    return tab;
  } catch {
    return null;
  }
}

function saveBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(anchor);
}

function openOrSaveBlob(blob, filename, pendingTab) {
  const url = window.URL.createObjectURL(blob);
  if (pendingTab && !pendingTab.closed) {
    pendingTab.location.href = url;
    window.setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    return;
  }

  const opened = window.open(url, '_blank', 'noopener,noreferrer');
  if (opened) {
    window.setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    return;
  }

  window.URL.revokeObjectURL(url);
  saveBlob(blob, filename);
}

function useFullSessionReportState({
  sessionId,
  sessionActive,
  conversationStarted,
  isOarsMode,
  isLlmModeSession = false,
  historicalAnalysisStatus = null,
  onHistoricalDownloadPdf = null,
  onHistoricalDownloadCsv = null,
  preferredAnalyticsVisible = true,
  onAnalyticsVisibleChange,
  onViewedTurnChange,
  onFeedbackPdfDownloaded,
  showNotification,
  t,
  openDownloadsInNewTab = false,
}) {
  const [analysisStatus, setAnalysisStatus] = useState(null);
  const [postSessionView, setPostSessionView] = useState('metrics');
  const analyticsVisible = useMemo(() => {
    if (!conversationStarted && isOarsMode) {
      return false;
    }
    return preferredAnalyticsVisible;
  }, [conversationStarted, isOarsMode, preferredAnalyticsVisible]);
  useEffect(() => {
    if (historicalAnalysisStatus) {
      setAnalysisStatus(historicalAnalysisStatus);
      return undefined;
    }

    if (!sessionId || sessionActive) {
      return undefined;
    }

    const pollInterval = setInterval(async () => {
      try {
        const data = isLlmModeSession
          ? await getLlmModeStatus()
          : await getSessionStatus();
        setAnalysisStatus(data);

        if (!data.running && !data.pdf_running && ['completed', 'error', 'cancelled'].includes(data.status)) {
          clearInterval(pollInterval);
          // The analysis pipeline burns credits; refresh the badge now
          // that its charges have landed.
          notifyCreditsChanged();
        }
      } catch (err) {
        console.error('Error polling analysis status:', err);
        clearInterval(pollInterval);
      }
    }, 1000);

    return () => clearInterval(pollInterval);
  }, [historicalAnalysisStatus, isLlmModeSession, sessionId, sessionActive]);

  useEffect(() => {
    if (historicalAnalysisStatus) {
      setPostSessionView('metrics');
      return;
    }

    if (sessionActive) {
      setPostSessionView('metrics');
      setAnalysisStatus(null);
    }
  }, [historicalAnalysisStatus, sessionActive]);

  const toggleAnalyticsVisibility = () => {
    const nextVisible = !analyticsVisible;
    if (!nextVisible) {
      onViewedTurnChange?.(null);
    }
    onAnalyticsVisibleChange?.(nextVisible);
  };

  const handleDownloadPdf = useCallback(async () => {
    const pendingTab = openDownloadsInNewTab ? openPendingDocumentTab() : null;
    if (onHistoricalDownloadPdf) {
      try {
        await onHistoricalDownloadPdf({ openInNewTab: openDownloadsInNewTab, pendingTab });
      } catch (err) {
        pendingTab?.close();
        console.error('Error downloading historical PDF:', err);
        showNotification(t('report_pdf_download_failed'));
      }
      return;
    }

    if (!sessionId) {
      pendingTab?.close();
      showNotification(t('report_pdf_not_available'));
      return;
    }

    try {
      const blob = await downloadPdf();
      if (openDownloadsInNewTab) {
        openOrSaveBlob(blob, 'session_report.pdf', pendingTab);
      } else {
        saveBlob(blob, 'session_report.pdf');
      }
      onFeedbackPdfDownloaded?.();
    } catch (err) {
      pendingTab?.close();
      console.error('Error downloading PDF:', err);
      showNotification(t('report_pdf_download_failed'));
    }
  }, [onFeedbackPdfDownloaded, onHistoricalDownloadPdf, openDownloadsInNewTab, sessionId, showNotification, t]);

  const handleDownloadCsv = useCallback(async () => {
    const pendingTab = openDownloadsInNewTab ? openPendingDocumentTab() : null;
    if (onHistoricalDownloadCsv) {
      try {
        await onHistoricalDownloadCsv({ openInNewTab: openDownloadsInNewTab, pendingTab });
      } catch (err) {
        pendingTab?.close();
        console.error('Error downloading historical transcript:', err);
        showNotification(t('report_csv_download_failed'));
      }
      return;
    }

    if (!sessionId) {
      pendingTab?.close();
      showNotification(t('report_csv_not_available'));
      return;
    }

    try {
      const blob = await downloadCsv();
      if (openDownloadsInNewTab) {
        openOrSaveBlob(blob, 'session_transcript.pdf', pendingTab);
      } else {
        saveBlob(blob, 'session_transcript.pdf');
      }
    } catch (err) {
      pendingTab?.close();
      console.error('Error downloading CSV:', err);
      showNotification(t('report_csv_download_failed'));
    }
  }, [onHistoricalDownloadCsv, openDownloadsInNewTab, sessionId, showNotification, t]);

  return {
    analysisStatus,
    analyticsVisible,
    postSessionView,
    setPostSessionView,
    toggleAnalyticsVisibility,
    handleDownloadPdf,
    handleDownloadCsv,
  };
}

export default useFullSessionReportState;
